import { CommitmentCompensationSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/CommitmentCompensationSection'
import { CompanyProfileSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/CompanyProfileSection'
import { FundingAskSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/FundingAskSection'
import { FundingPitchDeckSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/FundingPitchDeckSection'
import { FundingPitchSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/FundingPitchSection'
import { FundingTractionSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/FundingTractionSection'
import { OpportunitySection } from '@src/components/sco/Requests/RequestBuilder/FormSections/OpportunitySection'
import { RequestTypeSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/RequestTypeSection'
import { SettingsPublishSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/SettingsPublishSection'
import { UserProfileSection } from '@src/components/sco/Requests/RequestBuilder/FormSections/UserProfileSection'
import { groupRequestTypes } from '@src/constants/group-request-types'
import { GroupRequest } from '@src/graphql-types'

export type RequestBuilderFormProps = {
  groupRequest: GroupRequest
  upsertGroupRequest: (any) => any
  upsertGroupRequestMutation: (any) => any
  upsertInvestorSearch: (any) => any
  upsertInvestorSearchMutation: (any) => any
  upsertUserSearch: (any) => any
  upsertUserSearchMutation: (any) => any
  onPrevSection?: () => void
  onNextSection?: () => void
}

const getProgressForValues = (
  values: any[]
): {
  totalSteps: number
  completedSteps: number
  percentComplete: number
} => {
  const totalSteps = values.length
  const completedSteps = values.filter(Boolean).length
  const percentComplete =
    totalSteps > 0 ? Math.max(10, Math.round((100 * completedSteps) / (totalSteps || 1))) : 100

  return {
    totalSteps,
    completedSteps,
    percentComplete,
  }
}

export const requestBuilderSections = [
  {
    key: 'request-type',
    title: 'What do you want to accomplish?',
    FormComponent: RequestTypeSection,
    getProgress: (groupRequest: GroupRequest) => getProgressForValues([groupRequest.groupRequestTypeKey]),
    groupRequestTypeKeys: ['all'],
  },
  {
    key: 'user-info',
    title: 'About You',
    FormComponent: UserProfileSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        groupRequest.requestingUser.firstName,
        groupRequest.requestingUser.lastName,
        groupRequest.requestingUser.pictureUrl,
      ]),
    groupRequestTypeKeys: ['all'],
  },
  {
    key: 'fast-track-pitch-deck',
    title: '🔥 Fast Track - Upload Pitch Deck',
    FormComponent: FundingPitchDeckSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([groupRequest.investorSearch?.pitchDeckUrl]),
    groupRequestTypeKeys: groupRequestTypes.filter(t => t.key !== 'funding').map(t => t.key),
  },
  {
    key: 'fast-track-pitch-deck',
    title: '🔥 Fast Track - Upload Pitch Deck',
    FormComponent: FundingPitchDeckSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([groupRequest.investorSearch?.pitchDeckUrl]),
    groupRequestTypeKeys: ['funding'],
  },

  {
    key: 'company-info',
    title: 'About Your Startup',
    FormComponent: CompanyProfileSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        groupRequest.requestingCompany.name,
        groupRequest.requestingCompany.elevatorPitch,
        groupRequest.requestingCompany.industryKeys?.length > 0,
      ]),
    groupRequestTypeKeys: ['all'],
  },
  {
    key: 'needs-opportunity',
    title: 'The Opportunity',
    FormComponent: OpportunitySection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        groupRequest.needExplanation,
        groupRequest.valueToRequester,
        groupRequest.valueToResponder,
      ]),
    groupRequestTypeKeys: ['talent', 'advisor', 'service-provider', 'cofounder'],
  },
  {
    key: 'commitment-compensation',
    title: 'Commitment & Compensation',
    FormComponent: CommitmentCompensationSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([groupRequest.timeCommitmentTypeKeys, groupRequest.compensationTypeKeys]),
    groupRequestTypeKeys: ['talent', 'advisor', 'service-provider', 'cofounder'],
  },

  {
    key: 'funding-ask',
    title: 'Funding Ask',
    FormComponent: FundingAskSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([groupRequest.investorSearch.fundingGoalInDollars]),
    groupRequestTypeKeys: ['funding'],
  },
  {
    key: 'funding-pitch',
    title: 'Your Pitch',
    FormComponent: FundingPitchSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        groupRequest.investorSearch.problemStatement,
        groupRequest.investorSearch.solutionStatement,
        groupRequest.investorSearch.marketSizeStatement,
      ]),
    groupRequestTypeKeys: ['funding'],
  },
  {
    key: 'funding-traction',
    title: 'Traction',
    FormComponent: FundingTractionSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        groupRequest.investorSearch.tractionStatement,
        groupRequest.investorSearch.financialsStatement,
        groupRequest.investorSearch.teamStatement,
      ]),
    groupRequestTypeKeys: ['funding'],
  },

  // {
  //   key: 'stored-files',
  //   title: 'File Attachments',
  //   FormComponent: StoredFilesSection,
  //   getProgress: (groupRequest: GroupRequest) =>
  //     getProgressForValues([groupRequest.storedFiles?.items?.length > 0]),
  //   groupRequestTypeKeys: ['all'],
  // },
  {
    key: 'settings',
    title: 'Publishing & Settings',
    FormComponent: SettingsPublishSection,
    getProgress: (groupRequest: GroupRequest) =>
      getProgressForValues([
        !!groupRequest.publishedAt ||
          groupRequest.publishStatusKey === 'published' ||
          !!groupRequest.publishRequestedAt ||
          groupRequest.publishStatusKey === 'pending_approval',
      ]),
    groupRequestTypeKeys: ['all'],
  },
]
